import {wpApiClient} from '@/services/apiClient';

/**
 * DELETING AN ACCOUNT — two calls, and there is no way back from the second one.
 *
 * ── ⚠️ IT IS NOT AUTHENTICATED, AND THAT IS THE DESIGN ────────────────────────────
 * Neither endpoint reads a bearer token. The OTP itself is the proof: it is sent to the
 * contact the caller named, so holding the code proves control of that contact exactly as
 * an OTP login does. That is what lets ONE implementation serve the signed-in dashboard
 * and the public page Google Play requires — no session, no freshness rules, no second
 * contract to keep in step.
 *
 * The code is bound to a DELETION request row, so a login OTP can never delete an account
 * and a deletion code can never sign anyone in.
 *
 * ── ⚠️ THE TWO LEGS NEVER CROSS ───────────────────────────────────────────────────
 * `/passengers/...` deletes passengers and `/drivers/...` deletes drivers. A driver's email
 * sent to the passenger endpoint is treated as "no account" — and because step 1 is
 * enumeration-safe, that failure is SILENT: the caller is told a code was sent and then
 * waits for one that will never arrive. So the leg is a required argument here rather than
 * a default, to make picking it a decision the caller has to make.
 *
 * ── ⚠️ STEP 1 CANNOT TELL YOU WHETHER THE ACCOUNT EXISTS ──────────────────────────
 * It answers 200 with the same body either way, on purpose — an endpoint that said "no such
 * account" would be a free membership oracle for anyone with a list of phone numbers. Never
 * write UI that claims a contact was found.
 */

/** The two legs. Anything else is a caller bug, not a value to fall back from. */
export const DELETION_LEGS = {passenger: 'passengers', driver: 'drivers'};

/** What the server wants typed, literally and in capitals, before it will act. */
export const DELETION_CONFIRMATION = 'DELETE';

function legPath(leg) {
  const prefix = DELETION_LEGS[leg] || leg;
  if (prefix !== 'passengers' && prefix !== 'drivers') {
    throw new Error(`Unknown deletion leg: ${leg}`);
  }
  return prefix;
}

/**
 * Email or phone, as the API takes it.
 *
 * ⚠️ ONE FIELD OR THE OTHER, NEVER BOTH. The server matches on whichever is present, and
 * step 2 must repeat the SAME one step 1 used — a code requested by phone does not confirm
 * against an email, even for the same person.
 */
function contactBody(contact) {
  const value = String(contact ?? '').trim();
  return value.includes('@') ? {email: value} : {phone: value};
}

/**
 * The error the caller has to tell apart from every other.
 *
 * ⚠️ IT IS THE ONLY BUSINESS REFUSAL IN THE WHOLE FLOW. Upcoming rides, wallet credit,
 * driver balance, open offers — all of those are resolved by the backend and none of them
 * blocks a deletion. Only a ride actually IN PROGRESS does, and it is temporary: the same
 * code keeps working once the ride ends, so this is a "try again in a bit", not a refusal.
 */
export const DELETION_BLOCKED_ACTIVE_RIDE = 'deletion_blocked_active_ride';

/** Server keys that mean "that code is no good, ask for another one". */
const RESENDABLE_KEYS = new Set([
  'deletion_code_expired',
  'deletion_attempts_exceeded',
]);

/**
 * Turn a failure into something a person can act on.
 *
 * ⚠️ BRANCHED ON `key`, NEVER ON `message`. The message is prose the backend may reword;
 * the key is the contract. `resendable` is returned alongside the text because the right
 * BUTTON differs from the right sentence — an expired code needs "Send a new code", a wrong
 * one needs another attempt at the field.
 */
function describeFailure(result) {
  const key = result.raw?.key || null;
  const status = result.statusCode;

  if (key === DELETION_BLOCKED_ACTIVE_RIDE) {
    return {
      key,
      resendable: false,
      blockedByRide: true,
      error: 'You have a ride in progress. Your account can be deleted once that ride is completed.',
    };
  }
  if (key === 'deletion_code_expired') {
    return {key, resendable: true, error: 'That code has expired. Send a new one to try again.'};
  }
  if (key === 'deletion_attempts_exceeded') {
    return {key, resendable: true, error: 'Too many incorrect codes. Send a new one to try again.'};
  }
  if (key === 'deletion_code_invalid') {
    return {key, resendable: false, error: 'The verification code is invalid or has expired.'};
  }
  /* ⚠️ 429 WITHOUT A KEY IS THE PER-IP THROTTLE, not a wrong code — three requests a
     minute. Telling someone their code was wrong there would send them back to a field
     that is not the problem. */
  if (status === 429) {
    return {key, resendable: false, error: 'Too many attempts. Please wait a minute and try again.'};
  }
  if (status === 500) {
    return {key, resendable: false, error: 'Something went wrong and nothing was changed. Please try again shortly.'};
  }

  /* Field-level validation (a malformed contact, a 5-digit code) comes back in
     `data.errors`; the first one is more useful than a generic sentence. */
  const fieldError = Object.values(result.raw?.data?.errors || {})[0];
  return {
    key,
    resendable: RESENDABLE_KEYS.has(key),
    error: (Array.isArray(fieldError) ? fieldError[0] : fieldError)
      || result.error
      || 'That did not work. Please try again.',
  };
}

export const accountDeletionService = {
  /**
   * Step 1 — send a 6-digit code to the ONE contact given.
   *
   * It used to go to the phone AND the email. Since Sept 2026 the identifier IS the
   * channel: an email address gets an email, a number gets an SMS, and nothing reaches
   * the other one — which is why `contactBody` sends exactly one of them.
   *
   * ⚠️ SUCCESS HERE MEANS "ASKED", NOT "SENT". See the note at the top: the answer is
   * identical for a real account and an unknown contact. The only honest thing to show is
   * the server's own sentence, which is written for exactly this.
   */
  async requestCode(leg, contact) {
    const result = await wpApiClient.post(
      `/${legPath(leg)}/account/delete/request-otp`,
      contactBody(contact),
    );

    if (!result.success) return {success: false, ...describeFailure(result)};

    return {
      success: true,
      message: result.message
        || 'If an account exists for this contact, a verification code has been sent.',
      /* ⚠️ TEST ACCOUNTS ONLY, and the server decides which those are. Real accounts never
         echo the code back. Surfacing it is what makes an App Store review pass possible
         without handing the reviewer a phone. */
      testOtp: result.data?.otp ?? null,
      error: null,
    };
  },

  /**
   * Step 2 — the irreversible one.
   *
   * ⚠️ THE CONTACT MUST BE THE ONE FROM STEP 1. Not "the same person" — the same field. A
   * code requested against a phone number does not confirm against that account's email.
   *
   * @param {'passenger'|'driver'} leg
   * @param {string} contact  the email or phone used in step 1
   * @param {string} otp  six digits
   * @param {'ios'|'android'|'web'} [source]
   */
  async confirm(leg, contact, otp, source = 'web') {
    const result = await wpApiClient.post(`/${legPath(leg)}/account/delete/confirm`, {
      ...contactBody(contact),
      otp: String(otp ?? '').trim(),
      confirmation: DELETION_CONFIRMATION,
      source,
    });

    if (!result.success) return {success: false, ...describeFailure(result)};

    const deletion = result.data?.deletion || null;
    return {
      success: true,
      /* The reference is the only thing the user keeps. It also reaches them by SMS and
         email, so a lost screen is not a lost record — but it belongs on screen. */
      reference: deletion?.reference || null,
      message: result.message || null,
      error: null,
    };
  },
};

export default accountDeletionService;
